import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, firstValueFrom, takeUntil } from 'rxjs';
import { APP_CONFIG, AppConfig } from '../../../../app.config';
import { CognitoAuthService } from '../../../shared/services/cognito-auth.service';
import { globalMessages } from '../../../shared/global-message';
import { ModalService } from '../../../shared/services/modal.service';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private paymentUrl: string;
  private policyBaseUrl: string;
  private readonly destroy$ = new Subject<void>();
  quoteId: string = '';
  processingFeeEligible: boolean = false;

  constructor(
    private http: HttpClient,
    private cognitoAuth: CognitoAuthService,
    private modalService: ModalService,
    @Inject(APP_CONFIG) private config: AppConfig
  ) {
    this.paymentUrl = this.config.apiDomainURL + globalMessages.paymentURL || '';
    this.policyBaseUrl = this.config.apiDomainURL + globalMessages.policyURL || '';
  }

  private getAuthHeaders(): HttpHeaders {
    const idToken = this.cognitoAuth.getIdToken() || '';
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    });
  }

  private getBillingUrl(quoteId: string): string {
    return `${this.policyBaseUrl}/${quoteId}/billing`;
  }

  /**
   * Creates a OneInc session for payment
   * NOTE: Authorization header is added by HTTP interceptor
   */
  getPaymentSession(request: {
    quoteid: string;
    action: string;
    targetOperation: string;
  }): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post<any>(this.paymentUrl, request, { headers });
  }

  /**
   * Gets saved payment methods for customer
   * NOTE: Authorization header is added by HTTP interceptor
   */
  getSavedPaymentMethods(request: any): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post<any>(this.paymentUrl, request, { headers });
  }

  /**
   * Sets payment method as default
   * NOTE: Authorization header is added by HTTP interceptor
   */
  setDefaultPaymentMethod(request: {
    quoteid: string;
    action: string;
    customerId: string;
    tokenId: string;
  }): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post<any>(this.paymentUrl, request, { headers });
  }

  /**
   * Gets customer ID
   * NOTE: Authorization header is added by HTTP interceptor
   */
  getCustomerId(request: any): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post<any>(this.paymentUrl, request, { headers });
  }

  /**
   * Checks whether a processing fee applies for the policy's state / line of business
   */
  getProcessingFee(request: any): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post<any>(this.paymentUrl, request, { headers });
  }

  /**
   * Edits an existing saved payment method
   */
  editPaymentMethod(request: any): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post<any>(this.paymentUrl, request, { headers });
  }

  /**
   * Deletes a saved payment method
   */
  deletePaymentMethod(request: any): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post<any>(this.paymentUrl, request, { headers });
  }

  /**
   * Charges a saved payment method for the selected amount
   * NOTE: Authorization header is added by HTTP interceptor
   */
  chargePayment(request: any): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post<any>(this.paymentUrl, request, { headers });
  }

  /**
   * Processes a refund for a payment
   */
  refundPayment(request: any): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post<any>(this.paymentUrl, request, { headers });
  }

  /**
   * Updates billing data for a policy
   */
  updateBillingData(referenceId: string, request: any): Observable<any> {
    const headers = this.getAuthHeaders();
    const url = `${this.policyBaseUrl}/${referenceId}/billing`;
    return this.http.put<any>(url, request, { headers });
  }

  /**
   * If Processing Fee Eligible the display consent after selecting Credit Card
   * User Should not allow to add credit card without consent
   * Only applicable for Credit Card
   */
  async ccPaymentConsent(): Promise<boolean> {
    const paymentConsentModal = {
      type: globalMessages.modalTypeActionDialog,
      imageSrc: globalMessages.iconExclamationOctagonOrange,
      message: globalMessages.modalPaymentConsentTitle,
      messageExt: globalMessages.modalPaymentConsentBody,
      confirm: globalMessages.modalConfirmConfirm,
      cancel: globalMessages.modalCancelCancel,
      fields: [],
    };

    const response = await firstValueFrom(
      this.modalService.confirm(paymentConsentModal).pipe(takeUntil(this.destroy$))
    );

    return response?.[0] === globalMessages.modalConfirmConfirm;
  }

  getPaymentDetails() {
    //after submit payment charge response need to be updated in payment service
    return {};
  }
}
